import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAccount, useWalletClient, usePublicClient, useDisconnect } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { ethers } from 'ethers'
import { BrowserProvider, JsonRpcSigner } from 'ethers'
import './TornadoStyle.css'
import {
  BLUR_TOKEN_ABI,
  BLUR_MIXER_ABI,
  BLUR_MIXER_FIXED_ABI,
  BLUR_MIXER_ENCRYPTED_ABI,
  MOCK_USDT_ABI,
  USDT_WRAPPER_ABI
} from './contracts'
import BlurMixerFullyEncryptedV2ABI from './contracts/BlurMixerFullyEncryptedV2.json'
import USDTWrapperFullyEncryptedABI from './contracts/USDTWrapperFullyEncrypted.json'
import BlurMixerDirectABI from './contracts/BlurMixerDirect.json'
import ConfidentialUSDTABI from './contracts/ConfidentialUSDT.json'
import NullayerRewardsABI from './contracts/NullayerRewards.json'
import { MiningDashboard } from './MiningDashboard'
import { MyDashboard } from './MyDashboard'
import { Leaderboard } from './Leaderboard'
import { Modal, friendlyErrorMessage } from './Modal'
import { translations, type Language, getSavedLanguage, saveLanguage } from './i18n'
import { LanguageSwitch } from './LanguageSwitch'
import { Landing } from './Landing'
import { Navbar } from './Navbar'
import { saveWalletConnection, getSavedWalletAddress, clearWalletConnection } from './walletStorage'
import { clientToProvider, clientToSigner } from './ethersAdapters'
import { API_URL } from './config/api'

// 开发环境：在控制台暴露诊断工具
if (import.meta.env.DEV) {
  // 积分调试工具
  (window as any).checkRewards = async (address: string) => {
    console.log('🔍 检查地址:', address)

    // 检查 localStorage
    const commitments = JSON.parse(localStorage.getItem(`commitments_global`) || '[]')
    console.log('💾 localStorage 中的 commitments:', commitments)

    if (commitments.length === 0) {
      console.warn('⚠️ 未找到任何存款记录！')
      console.log('提示：如果是之前的存款，需要重新存款才能记录到积分系统')
      return
    }

    // 查询合约数据
    try {
      if (typeof window.ethereum === 'undefined') {
        console.error('❌ 未检测到钱包')
        return
      }

      const rewardsAddress = '0xE719EF418eCF570E95E5Ca75c2C3FD87727d44Cc'
      const rewardsABI = [
        'function deposits(bytes32) view returns (uint256 amount, uint256 depositTime, bool withdrawn, bool rewardsClaimed, uint256 multiplier)',
        'function totalDeposits() view returns (uint256)'
      ]

      const provider = new ethers.BrowserProvider(window.ethereum)
      const contract = new ethers.Contract(rewardsAddress, rewardsABI, provider)

      console.log('📊 查询合约数据...')
      for (const item of commitments) {
        console.log('\n--- Commitment:', item.commitment)
        try {
          const depositInfo = await contract.deposits(item.commitment)
          console.log('✅ 合约数据:', {
            amount: depositInfo.amount.toString(),
            depositTime: new Date(Number(depositInfo.depositTime) * 1000).toLocaleString(),
            withdrawn: depositInfo.withdrawn,
            rewardsClaimed: depositInfo.rewardsClaimed,
            multiplier: Number(depositInfo.multiplier)
          })

          // 计算积分
          if (Number(depositInfo.depositTime) > 0) {
            const duration = Math.floor((Date.now() - Number(depositInfo.depositTime) * 1000) / 1000 / 86400)
            const amount = Number(item.amount)
            const basePoints = (amount * duration) / 1e6
            const pointsWithMultiplier = (basePoints * Number(depositInfo.multiplier)) / 100

            let bonus = 100
            if (amount >= 10000e6) bonus = 200
            else if (amount >= 5000e6) bonus = 150
            else if (amount >= 1000e6) bonus = 120

            const finalPoints = (pointsWithMultiplier * bonus) / 100

            console.log('💰 积分计算:', {
              amount: amount / 1e6,
              duration: duration + ' 天',
              multiplier: Number(depositInfo.multiplier),
              bonus: bonus + '%',
              finalPoints: Math.floor(finalPoints)
            })
          }
        } catch (err) {
          console.error('❌ 查询失败:', err)
        }
      }

      const totalDeposits = await contract.totalDeposits()
      console.log('\n📈 总存款笔数:', totalDeposits.toString())

    } catch (error) {
      console.error('❌ 合约查询失败:', error)
    }
  };

  console.log('💡 积分调试工具已加载！在控制台运行: checkRewards("你的地址")');
}

// BLUR 系统合约地址
const BLUR_ADDRESSES = {
  mixer: '0xfeCA2D6cC79c9E40F7556984376d31E120Dd2E6F', // BlurMixerTest (v2)
  token: '0x7D95cD9c9184e4c245db43470ffAeC33108e98Bf', // BlurTokenTest (v2)
}

// USDT 系统合约地址
const USDT_ADDRESSES = {
  usdt: '0x4F9435B6Fd662D561658D21670A7FB3C624EA1a7',      // MockUSDT
  wrapper: '0x8a02Fb2CEf74B823c609ED8867d88ce142E1336d',   // USDTWrapperTest (cUSDT - 旧版，Transfer有金额)
  mixerFixed: '0x9198caf1a8A749DDa818aAa19E79FB563956e19a',     // BlurMixerFixed (固定金额池)
  mixerEncrypted: '0x33A11325cB86E3Bb0e88484FC0ec1b9D47bE215c',  // BlurMixerEncrypted (加密金额池)
  mixerFullyEncrypted: '0x6bf4Df4E8F05892c7e98a55a061Ba10C6C4edE75',  // BlurMixerFullyEncryptedV2 (完全加密) - 使用旧wrapper

  // ✨ 新的 Confidential USDT (基于 Zama) ⭐
  cUSDT: '0xf28129C1D2ae3d1117165d02a6cFE4941640f4Ed',  // ConfidentialUSDT (支持 revealBalance)

  // 完整端到端隐私系统 ⭐ 使用新的 cUSDT + FHE 风格加密 + 重加密隐私
  wrapperFullyEncrypted: '0xf28129C1D2ae3d1117165d02a6cFE4941640f4Ed',  // ConfidentialUSDT (cUSDT with reveal)
  mixerCompletePrivacy: '0x3A4ce6E93CA0d613197B0eE32bfcF65B897a34a4',  // BlurMixerFullyEncryptedV2 (支持重加密 + 提取手续费)

  // 简化混币器 🚀
  mixerDirect: '0x0e49f5b4910FF93d87E32d8EF8f45f59355472D8',  // BlurMixerDirect (直接 USDT，无需 wrapper)

  mixer: '0x3A4ce6E93CA0d613197B0eE32bfcF65B897a34a4',          // 默认使用 Complete Privacy (支持重加密 + 提取手续费)

  // 💎 积分挖矿系统
  nullayerToken: '0x08553cCCB001e37CE0dE8a0cBC1cb2330B7C123d',  // NULLAYER 代币
  nullayerRewards: '0xE719EF418eCF570E95E5Ca75c2C3FD87727d44Cc',  // NullayerRewards 合约
}

// 固定金额池配置
const DENOMINATIONS = [
  { index: 0, amount: '100', label: '100 USDT', description: '小额测试' },
  { index: 1, amount: '1000', label: '1,000 USDT', description: '推荐使用', recommended: true },
  { index: 2, amount: '10000', label: '10,000 USDT', description: '大额交易' },
]

// 向后兼容
const CONTRACT_ADDRESSES = BLUR_ADDRESSES

function App() {
  const navigate = useNavigate()
  const location = useLocation()

  // Wagmi hooks
  const { address: account, isConnected: connected, chain } = useAccount()
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()
  const { disconnect } = useDisconnect()
  const { openConnectModal } = useConnectModal()

  const [provider, setProvider] = useState<any>(null)
  const [signer, setSigner] = useState<any>(null)
  const [rewardsContract, setRewardsContract] = useState<any>(null)
  const [depositAmount, setDepositAmount] = useState('')
  const [secret, setSecret] = useState('')
  const [nullifier, setNullifier] = useState('')
  const [commitment, setCommitment] = useState('')
  const [recipient, setRecipient] = useState('')
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [tokenBalance, setTokenBalance] = useState('0')
  const [ethBalance, setEthBalance] = useState('0')
  const [approved, setApproved] = useState(false)
  const [fhevmReady, setFhevmReady] = useState(false)

  // Derived values from wagmi
  const chainId = chain?.id.toString() || ''
  const networkName = chain?.name || ''

  // USDT 相关状态 (移除 blur tab)
  const [activeTab] = useState<'usdt'>('usdt')
  const [usdtBalance, setUsdtBalance] = useState('0')
  const [cUsdtBalance, setCUsdtBalance] = useState('0')
  const [cUsdtBalanceRevealed, setCUsdtBalanceRevealed] = useState(false) // 余额是否显示
  const [usdtApproved, setUsdtApproved] = useState(false)
  const [wrapAmount, setWrapAmount] = useState('')

  // 固定金额池相关
  const [selectedDenomination, setSelectedDenomination] = useState(1) // 默认选择 1000 USDT
  const [anonymitySet, setAnonymitySet] = useState(0)
  const [amountError, setAmountError] = useState('')

  // Modal 状态
  const [modalOpen, setModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalMessage, setModalMessage] = useState('')
  const [modalDetails, setModalDetails] = useState<string | undefined>(undefined)
  const [modalType, setModalType] = useState<'error' | 'warning' | 'success' | 'info'>('info')

  // 语言状态
  const [language, setLanguage] = useState<Language>(getSavedLanguage())
  const t = translations[language]

  // 固定使用 Complete Privacy 模式
  const mixerMode = 'complete-privacy'
  const [encryptedAmount, setEncryptedAmount] = useState('')  // 加密金额（用于 commitment 和取款）
  const [globalAnonymitySet, setGlobalAnonymitySet] = useState(0)  // 全局匿名集

  // 语言切换处理函数
  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang)
    saveLanguage(newLang)
  }

  // 显示 Modal 辅助函数
  const showModal = (title: string, message: string, type: 'error' | 'warning' | 'success' | 'info' = 'info', details?: string) => {
    setModalTitle(title)
    setModalMessage(message)
    setModalType(type)
    setModalDetails(details)
    setModalOpen(true)
  }

  const showErrorModal = (error: any) => {
    const { title, message, details } = friendlyErrorMessage(error, language)
    showModal(title, message, 'error', details)
  }

  // 连接钱包
  // 更新余额
  const updateBalance = async (address: string, prov: any) => {
    try {
      // 先检查网络
      const network = await prov.getNetwork()
      console.log('当前网络:', network.name, 'Chain ID:', network.chainId.toString())

      if (network.chainId !== 11155111n) {
        setStatus('⚠️ Please switch to Sepolia testnet! Current network: ' + network.name + ' (Chain ID: ' + network.chainId.toString() + ')')
        setTokenBalance('0')
        setEthBalance('0')
        return
      }

      // 获取 ETH 余额
      const ethBal = await prov.getBalance(address)
      setEthBalance(ethers.formatEther(ethBal))
      console.log('ETH 余额:', ethers.formatEther(ethBal))

      // 检查合约是否存在
      const code = await prov.getCode(CONTRACT_ADDRESSES.token)
      if (code === '0x') {
        console.error('合约不存在于当前网络')
        setStatus('❌ Contract not deployed on current network, please switch to Sepolia')
        setTokenBalance('0')
        return
      }

      // 获取代币余额（注意：这是加密的余额句柄）
      const tokenContract = new ethers.Contract(CONTRACT_ADDRESSES.token, BLUR_TOKEN_ABI, prov)
      const balance = await tokenContract.balanceOf(address)
      setTokenBalance(balance.toString())
      console.log('BLUR 余额句柄:', balance.toString())
    } catch (error: any) {
      console.error('获取余额失败:', error)

      // 更详细的错误提示
      if (error.message.includes('could not decode result data')) {
        setStatus(t.statusContractCallFailed)
      } else if (error.message.includes('network')) {
        setStatus(t.statusNetworkError)
      } else {
        setStatus('⚠️ Failed to fetch balance: ' + error.message)
      }

      setTokenBalance('0')
    }
  }

  // 更新 USDT 余额
  const updateUsdtBalance = async (address: string, prov: any) => {
    try {
      const usdt = new ethers.Contract(USDT_ADDRESSES.usdt, MOCK_USDT_ABI, prov)
      const usdtBal = await usdt.balanceOf(address)
      setUsdtBalance(ethers.formatUnits(usdtBal, 6))

      // cUSDT 余额：默认隐藏，需要用户点击眼睛按钮查看
      // 注意：ConfidentialUSDT.balanceOf() 返回 bytes32（加密hash），不是实际余额
      // 要查看实际余额，必须使用 signer 调用 revealBalance()
      setCUsdtBalance('Private')
      setCUsdtBalanceRevealed(false)

      console.log('USDT 余额:', ethers.formatUnits(usdtBal, 6))
      console.log('cUSDT 余额: Private (点击 👁️ 查看)')
    } catch (error: any) {
      console.error('获取 USDT 余额失败:', error)
      setUsdtBalance('0')
      setCUsdtBalance('Private')
      setCUsdtBalanceRevealed(false)
    }
  }

  // 解密/显示 cUSDT 余额（使用 signer）
  const revealCUsdtBalance = async () => {
    if (!signer || !account) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    try {
      // 使用新的 ConfidentialUSDT 合约
      const cUSDT = new ethers.Contract(
        USDT_ADDRESSES.cUSDT,
        ConfidentialUSDTABI.abi,
        signer
      )

      // 调用 revealBalance() 解密并显示自己的余额
      const balance = await cUSDT.revealBalance()
      const formattedBalance = ethers.formatUnits(balance, 6)

      setCUsdtBalance(formattedBalance)
      setCUsdtBalanceRevealed(true)

      console.log('✨ 解密余额成功:', formattedBalance)
    } catch (error: any) {
      console.error('解密余额失败:', error)
      showModal(
        language === 'zh' ? '解密失败' : 'Decrypt Failed',
        language === 'zh' ? '无法查询余额，请确保已连接钱包并已封装 USDT' : 'Cannot query balance, please ensure wallet is connected and USDT is wrapped',
        'error'
      )
    }
  }

  // 隐藏 cUSDT 余额
  const hideCUsdtBalance = () => {
    setCUsdtBalance('Private')
    setCUsdtBalanceRevealed(false)
  }

  // 检查 cUSDT 授权额度
  const checkCUsdtApproval = async (address: string, prov: any) => {
    try {
      // Complete Privacy 模式的合约地址
      const wrapperAddress = USDT_ADDRESSES.wrapperFullyEncrypted
      const wrapperABI = USDTWrapperFullyEncryptedABI.abi
      const mixerAddress = USDT_ADDRESSES.mixerCompletePrivacy

      const wrapper = new ethers.Contract(wrapperAddress, wrapperABI, prov)
      const allowance = await wrapper.allowance(address, mixerAddress)

      console.log('cUSDT Allowance:', ethers.formatUnits(allowance, 6))

      // 检查是否是无限授权（通常 > 10^20 就可以认为是无限授权）
      const unlimitedThreshold = ethers.parseUnits('1000000000000', 6) // 1 trillion USDT

      if (allowance >= unlimitedThreshold) {
        setUsdtApproved(true)
        console.log('✨ cUSDT 已授权（无限额度）')
      } else if (depositAmount && allowance >= ethers.parseUnits(depositAmount, 6)) {
        setUsdtApproved(true)
        console.log('✨ cUSDT 已授权（额度充足）')
      } else {
        setUsdtApproved(false)
        console.log('❌ cUSDT 未授权或额度不足')
      }
    } catch (error: any) {
      console.error('检查授权失败:', error)
      setUsdtApproved(false)
    }
  }

  // 更新匿名集大小（固定金额池）
  const updateAnonymitySet = async (denominationIndex: number, prov: any) => {
    try {
      const mixer = new ethers.Contract(USDT_ADDRESSES.mixerFixed, BLUR_MIXER_FIXED_ABI, prov)
      const size = await mixer.getAnonymitySetSize(denominationIndex)
      setAnonymitySet(Number(size))
      console.log(`池 ${denominationIndex} 匿名集:`, Number(size))
    } catch (error: any) {
      console.error('获取匿名集失败:', error)
      setAnonymitySet(0)
    }
  }

  // 更新全局匿名集大小（加密金额池 / 完全加密池 / 完整隐私）
  const updateGlobalAnonymitySet = async (prov: any) => {
    try {
      // 根据当前模式选择正确的合约
      let mixerAddress
      let mixerABI

      // Complete Privacy 模式
      mixerAddress = USDT_ADDRESSES.mixerCompletePrivacy
      mixerABI = BlurMixerFullyEncryptedV2ABI.abi

      const mixer = new ethers.Contract(mixerAddress, mixerABI, prov)
      const size = await mixer.getAnonymitySetSize()
      setGlobalAnonymitySet(Number(size))
      console.log('全局匿名集:', Number(size))
    } catch (error: any) {
      console.error('获取全局匿名集失败:', error)
      setGlobalAnonymitySet(0)
    }
  }

  // 验证存款金额是否匹配固定金额
  const validateDepositAmount = (amount: string, denominationIndex: number): boolean => {
    const expectedAmount = DENOMINATIONS[denominationIndex].amount
    if (amount !== expectedAmount) {
      setAmountError(`❌ 金额必须是 ${expectedAmount} USDT！当前池只接受固定金额。`)
      return false
    }
    setAmountError('')
    return true
  }

  // Wrap USDT → cUSDT
  const wrapUsdt = async () => {
    if (!signer || !account) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    if (!wrapAmount || parseFloat(wrapAmount) <= 0) {
      showModal(t.modalTitleEnterAmount, t.modalMsgEnterAmount, 'warning')
      return
    }

    setLoading(true)
    setStatus(language === 'zh' ? '🔄 正在封装 USDT...' : '🔄 Wrapping USDT...')

    try {
      // 使用新的 ConfidentialUSDT 合约
      const wrapperAddress = USDT_ADDRESSES.cUSDT
      const wrapperABI = ConfidentialUSDTABI.abi

      const usdt = new ethers.Contract(USDT_ADDRESSES.usdt, MOCK_USDT_ABI, signer)
      const wrapper = new ethers.Contract(wrapperAddress, wrapperABI, signer)
      const amount = ethers.parseUnits(wrapAmount, 6)

      // 检查余额
      const balance = await usdt.balanceOf(account)
      if (balance < amount) {
        setStatus(language === 'zh' ? '❌ USDT 余额不足' : '❌ Insufficient USDT balance')
        setLoading(false)
        return
      }

      // Approve USDT
      const wrapperName = language === 'zh'
        ? (mixerMode === 'complete-privacy' ? '新 cUSDT (完全加密)' : 'cUSDT')
        : 'cUSDT'
      setStatus(language === 'zh' ? `📝 授权 USDT 给 ${wrapperName}...` : `📝 Approving USDT for ${wrapperName}...`)
      let tx = await usdt.approve(wrapperAddress, amount)
      await tx.wait()

      // Wrap
      setStatus(language === 'zh' ? `🔄 正在封装 USDT → ${wrapperName}...` : `🔄 Wrapping USDT → ${wrapperName}...`)
      tx = await wrapper.wrap(amount)
      const receipt = await tx.wait()

      setStatus(language === 'zh'
        ? `✨ 封装成功！交易: ${receipt.hash.substring(0, 10)}... (${wrapperName})`
        : `✨ Wrap successful! Tx: ${receipt.hash.substring(0, 10)}... (${wrapperName})`
      )
      await updateUsdtBalance(account, provider)

      // 🔧 如果用户之前显示了余额，自动刷新
      if (cUsdtBalanceRevealed) {
        await revealCUsdtBalance()
      }
    } catch (error: any) {
      console.error(language === 'zh' ? '封装失败:' : 'Wrap failed:', error)
      showErrorModal(error)
    } finally {
      setLoading(false)
    }
  }

  // Unwrap cUSDT → USDT
  const unwrapUsdt = async () => {
    if (!signer || !account) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    if (!wrapAmount || parseFloat(wrapAmount) <= 0) {
      showModal(t.modalTitleEnterAmount, t.modalMsgEnterAmount, 'warning')
      return
    }

    setLoading(true)
    setStatus(language === 'zh' ? '🔄 正在解封装 cUSDT...' : '🔄 Unwrapping cUSDT...')

    try {
      // 使用新的 ConfidentialUSDT 合约
      const cUSDT = new ethers.Contract(USDT_ADDRESSES.cUSDT, ConfidentialUSDTABI.abi, signer)
      const amount = ethers.parseUnits(wrapAmount, 6)

      // 检查 cUSDT 余额（使用 revealBalance）
      const balance = await cUSDT.revealBalance()
      if (balance < amount) {
        setStatus(language === 'zh' ? '❌ cUSDT 余额不足' : '❌ Insufficient cUSDT balance')
        setLoading(false)
        return
      }

      // Unwrap
      setStatus(language === 'zh' ? '🔄 正在解封装 cUSDT → USDT...' : '🔄 Unwrapping cUSDT → USDT...')
      const tx = await cUSDT.unwrap(amount)
      const receipt = await tx.wait()

      setStatus(language === 'zh'
        ? `✨ 解封装成功！交易: ${receipt.hash.substring(0, 10)}...`
        : `✨ Unwrap successful! Tx: ${receipt.hash.substring(0, 10)}...`
      )
      await updateUsdtBalance(account, provider)

      // 🔧 如果用户之前显示了余额，自动刷新
      if (cUsdtBalanceRevealed) {
        await revealCUsdtBalance()
      }
    } catch (error: any) {
      console.error(language === 'zh' ? '解封装失败:' : 'Unwrap failed:', error)
      showErrorModal(error)
    } finally {
      setLoading(false)
    }
  }

  // Approve cUSDT 给 Mixer（无限额度）
  const approveCUsdtForMixer = async () => {
    if (!signer || !account) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    setLoading(true)

    // Complete Privacy 模式
    const wrapperAddress = USDT_ADDRESSES.wrapperFullyEncrypted
    const wrapperABI = USDTWrapperFullyEncryptedABI.abi
    const mixerAddress = USDT_ADDRESSES.mixerCompletePrivacy
    const wrapperName = 'cUSDT'
    setStatus(`📝 Approving ${wrapperName} (unlimited)...`)

    try {
      const wrapper = new ethers.Contract(wrapperAddress, wrapperABI, signer)

      // 授权无限额度（type(uint256).max）
      const maxAmount = ethers.MaxUint256

      const tx = await wrapper.approve(mixerAddress, maxAmount)
      const receipt = await tx.wait()

      setStatus(`✨ ${wrapperName} approved successfully (unlimited)! Tx: ` + receipt.hash.substring(0, 10) + '...')

      // 重新检查授权状态
      await checkCUsdtApproval(account, provider)
    } catch (error: any) {
      console.error('授权失败:', error)
      if (error.message.includes('user rejected')) {
        setStatus('❌ User rejected approval')
      } else {
        setStatus('❌ Approval failed: ' + error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // 存款 cUSDT 到 Mixer
  const depositCUsdt = async () => {
    // 检查是否生成密钥
    if (!commitment || !secret || !nullifier) {
      showModal(
        t.modalTitleGenerateKeys,
        t.modalMsgGenerateKeys,
        'warning'
      )
      return
    }

    // 加密模式：检查 encryptedAmount
    if ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && !encryptedAmount) {
      showModal(
        t.modalTitleGenerateKeys,
        language === 'zh' ? '加密金额模式需要重新生成密钥（包含金额）' : 'Encrypted mode requires regenerating keys with amount',
        'warning'
      )
      return
    }

    // 检查钱包连接
    if (!signer || !account) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    // 检查金额
    if (!depositAmount) {
      showModal(t.modalTitleEnterAmount, t.modalMsgEnterAmount, 'warning')
      return
    }

    // 固定金额模式：验证金额是否匹配
    if (mixerMode === 'fixed' && !validateDepositAmount(depositAmount, selectedDenomination)) {
      const expectedAmount = DENOMINATIONS[selectedDenomination].amount
      showModal(
        t.modalTitleWrongAmount,
        t.modalMsgWrongAmount.replace('{amount}', expectedAmount),
        'warning'
      )
      return
    }

    // 检查授权
    if (!usdtApproved) {
      showModal(
        t.modalTitleApprove,
        t.modalMsgApprove,
        'warning'
      )
      return
    }

    // 🔧 检查 encryptedAmount 是否匹配当前金额（加密模式）
    if ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && encryptedAmount) {
      const currentAmountBigInt = ethers.parseUnits(depositAmount, 6)

      // ✨ 新逻辑：从 FHE 风格密文中提取金额（bytes 20-28）
      // 格式：0x[20_byte_handle][8_byte_amount][1_byte_padding]010500
      let amountInKeys: bigint

      try {
        // 检查是否是 FHE 风格格式（以 010500 结尾）
        if (encryptedAmount.endsWith('010500')) {
          // FHE 格式：从 bytes 20-28 提取金额 (16个hex字符 = 8字节)
          // 0x + 40个字符(20字节handle) + 16个字符(8字节amount) + ...
          const amountHex = '0x' + encryptedAmount.slice(42, 58) // 提取 8 字节金额
          amountInKeys = BigInt(amountHex)
        } else {
          // 旧格式：直接解码
          amountInKeys = BigInt(encryptedAmount)
        }

        if (amountInKeys !== currentAmountBigInt) {
          showModal(
            language === 'zh' ? '金额不匹配' : 'Amount Mismatch',
            language === 'zh'
              ? `当前输入金额 ${depositAmount} 与密钥中的金额不匹配。请重新生成密钥！`
              : `Current amount ${depositAmount} does not match the amount in keys. Please regenerate keys!`,
            'warning'
          )
          return
        }
      } catch (error) {
        console.error('Failed to parse encrypted amount:', error)
        showModal(
          language === 'zh' ? '密钥格式错误' : 'Invalid Key Format',
          language === 'zh'
            ? '无法解析密钥中的金额，请重新生成密钥！'
            : 'Cannot parse amount from keys. Please regenerate keys!',
          'warning'
        )
        return
      }
    }

    setLoading(true)

    try {
      setStatus('↓ Depositing cUSDT...')

      // 根据模式选择不同的合约
      const mixerAddress = mixerMode === 'complete-privacy' ?
        USDT_ADDRESSES.mixerCompletePrivacy :
        (mixerMode === 'fully-encrypted' ?
          USDT_ADDRESSES.mixerFullyEncrypted :
          (mixerMode === 'encrypted' ?
            USDT_ADDRESSES.mixerEncrypted :
            USDT_ADDRESSES.mixerFixed))

      const mixerABI = (mixerMode === 'complete-privacy' || mixerMode === 'fully-encrypted') ?
        BlurMixerFullyEncryptedV2ABI.abi :
        (mixerMode === 'encrypted' ?
          BLUR_MIXER_ENCRYPTED_ABI :
          BLUR_MIXER_FIXED_ABI)

      const mixer = new ethers.Contract(mixerAddress, mixerABI, signer)
      const amount = BigInt(ethers.parseUnits(depositAmount, 6))

      // ✨ 准备 FHE 加密输入（Zama 风格）
      // encryptedAmount: FHE 密文（Sepolia 上是 ABI 编码的 bytes32）
      // inputProof: 验证证明（Sepolia 上是空 '0x'）
      const encAmount = (mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy')
        ? encryptedAmount
        : ethers.toBeHex(amount, 32)
      const inputProof = generateInputProof()

      setStatus('↓ Sending deposit transaction...')

      let tx
      if (mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') {
        // 加密金额模式：不需要 denominationIndex
        tx = await mixer.deposit(commitment, encAmount, inputProof)
      } else {
        // 固定金额模式：需要 denominationIndex
        tx = await mixer.deposit(selectedDenomination, commitment, encAmount, inputProof)
      }

      setStatus('⏳ Waiting for deposit confirmation...')
      const receipt = await tx.wait()

      const modeText = mixerMode === 'complete-privacy' ?
        `${depositAmount} cUSDT (${language === 'zh' ? '完整隐私系统 ↓⭐' : 'Complete Privacy System ↓⭐'})` :
        (mixerMode === 'fully-encrypted' ?
          `${depositAmount} cUSDT (${language === 'zh' ? '完全加密池 ⭐' : 'Fully Encrypted Pool ⭐'})` :
          (mixerMode === 'encrypted' ?
            `${depositAmount} cUSDT (${language === 'zh' ? '加密金额池' : 'Encrypted Pool'})` :
            `${depositAmount} cUSDT (${language === 'zh' ? '固定金额池' : 'Fixed Pool'})`))


      setStatus(`✨ Deposit successful! Tx: ${receipt.hash}`)
      showModal(
        language === 'zh' ? '存款成功' : 'Deposit Successful',
        `${language === 'zh' ? '已成功存入' : 'Successfully deposited'} ${modeText}`,
        'success'
      )

      setDepositAmount('')

      await updateUsdtBalance(account, provider)

      // 🔧 更新 cUSDT 余额（存款后 cUSDT 减少）
      if (cUsdtBalanceRevealed) {
        // 如果余额是显示状态，重新查询
        await revealCUsdtBalance()
      } else {
        // 如果是隐藏状态，保持隐藏（但标记余额已改变）
        setCUsdtBalance('Private')
        setCUsdtBalanceRevealed(false)
      }

      // 根据模式更新匿名集
      if (mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') {
        await updateGlobalAnonymitySet(provider)
      } else {
        await updateAnonymitySet(selectedDenomination, provider)
      }

      await checkCUsdtApproval(account, provider)

      // 保存 commitment 到本地用于积分追踪
      try {
        const savedCommitments = JSON.parse(localStorage.getItem(`commitments_global`) || '[]')
        // 检查是否已存在
        const exists = savedCommitments.some((c: any) => c.commitment === commitment)
        if (!exists) {
          savedCommitments.push({
            commitment,
            depositTime: Date.now(),
            amount: depositAmount
          })
          localStorage.setItem(`commitments_global`, JSON.stringify(savedCommitments))
          console.log('✅ Commitment 已保存到 localStorage:', commitment)
        }
      } catch (e) {
        console.error('❌ 保存 commitment 失败:', e)
      }

      setTimeout(() => {
        setStatus('⚠️ Please save your key file! You can withdraw in 10 seconds')
      }, 3000)
    } catch (error: any) {
      console.error('存款失败:', error)
      showErrorModal(error)
    } finally {
      setLoading(false)
    }
  }

  // 从 Mixer 取款 cUSDT
  const withdrawCUsdt = async () => {
    if (!signer) {
      showModal(t.modalTitleConnectWallet, t.modalMsgConnectWallet, 'warning')
      return
    }

    if (!secret || !nullifier) {
      showModal(
        t.modalTitleEnterKeys,
        t.modalMsgEnterKeys,
        'warning'
      )
      return
    }

    // Complete Privacy 模式需要 encryptedAmount
    if (!encryptedAmount) {
      showModal(
        t.modalTitleEnterKeys,
        language === 'zh' ? '需要提供 encryptedAmount（从密钥文件中获取）' : 'Requires encryptedAmount from key file',
        'warning'
      )
      return
    }

    if (!recipient) {
      showModal(t.modalTitleEnterAddress, t.modalMsgEnterAddress, 'warning')
      return
    }

    setLoading(true)
    setStatus('↑ Withdrawing...')

    try {
      // Complete Privacy 模式合约
      const mixerAddress = USDT_ADDRESSES.mixerCompletePrivacy
      const mixerABI = BlurMixerFullyEncryptedV2ABI.abi
      const mixer = new ethers.Contract(mixerAddress, mixerABI, signer)

      // ✨ Complete Privacy 模式：重新加密金额以提高隐私性
        // 从原始 encryptedAmount 中提取金额，然后用新的随机 handle 重新加密

        let reEncryptedAmount: string

        // 检查是否是 FHE 格式（以 010500 结尾）
        if (encryptedAmount.endsWith('010500')) {
          // FHE 格式：从 bytes 20-27 提取金额
          const amountHex = '0x' + encryptedAmount.slice(42, 58) // 8 bytes
          const amountBigInt = BigInt(amountHex)

          // 生成新的随机 handle（不同于 deposit 时的 handle）
          const randomBytes = new Uint8Array(20)
          crypto.getRandomValues(randomBytes)
          const newHandle = Array.from(randomBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')

          // 用相同金额但不同 handle 创建新密文
          const amountHexStr = amountBigInt.toString(16).padStart(16, '0')
          const typeMarker = '010500'
          const padding = '00'

          reEncryptedAmount = '0x' + newHandle + amountHexStr + padding + typeMarker
          console.log('✨ Re-encrypted amount for withdraw:', reEncryptedAmount)
          console.log('   (Different from deposit for privacy!)')
        } else {
          // Legacy 格式：直接使用
          reEncryptedAmount = encryptedAmount
        }

        const inputProof = '0x'
        const tx = await mixer.withdraw(secret, nullifier, reEncryptedAmount, inputProof, recipient)

      setStatus('⏳ Waiting for withdrawal confirmation...')
      const receipt = await tx.wait()

      const modeText = `(${language === 'zh' ? '完整隐私系统 ↓⭐' : 'Complete Privacy System ↓⭐'})`

      setStatus(`✨ Withdrawal successful! cUSDT sent to ${recipient.substring(0, 6)}... ${modeText}`)
      showModal(
        language === 'zh' ? '取款成功' : 'Withdrawal Successful',
        `${language === 'zh' ? 'cUSDT 已发送到' : 'cUSDT sent to'} ${recipient.substring(0, 6)}...${recipient.substring(38)}`,
        'success'
      )

      // 更新后端积分记录
      try {
        const commitments = JSON.parse(localStorage.getItem(`commitments_global`) || '[]')

        // 计算当前 withdraw 的 commitment（从 secret + nullifier）
        const withdrawCommitment = ethers.keccak256(
          ethers.solidityPacked(['bytes32', 'bytes32'], [secret, nullifier])
        )

        // 通过 commitment 精确匹配
        const matchedCommitment = commitments.find((c: any) =>
          c.commitment.toLowerCase() === withdrawCommitment.toLowerCase()
        )

        console.log('🔍 Matching commitment (Complete Privacy):', {
          withdrawCommitment,
          savedCommitments: commitments.map((c: any) => c.commitment),
          matched: !!matchedCommitment
        })

        if (matchedCommitment) {
          const response = await fetch(`${API_URL}/api/update-deposit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              commitment: matchedCommitment.commitment,
              address: recipient,
              amount: parseInt(matchedCommitment.amount)
            })
          })

          if (response.ok) {
            const result = await response.json()
            console.log(`✅ ${t.pointsUpdated}:`, result.points_earned)
            setStatus(`✨ ${t.withdrawSuccessPoints} ${result.points_earned.toFixed(2)} ${t.pointsEarned} 🎁 (${t.completePrivacySystem})`)
          }
        } else {
          console.warn('⚠️ 未找到匹配的存款记录')
        }
      } catch (apiError) {
        console.error('积分更新失败:', apiError)
        // 不影响主流程
      }

      setSecret('')
      setNullifier('')
      setRecipient('')
      setEncryptedAmount('')

      await updateUsdtBalance(account, provider)

      // 🔧 更新 cUSDT 余额（取款获得的是 cUSDT）
      if (cUsdtBalanceRevealed) {
        // 如果余额是显示状态，重新查询
        await revealCUsdtBalance()
      } else {
        // 如果是隐藏状态，保持隐藏（但标记余额已改变）
        setCUsdtBalance('Private')
        setCUsdtBalanceRevealed(false)
      }

      // 根据模式更新匿名集
      if (mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') {
        await updateGlobalAnonymitySet(provider)
      } else {
        await updateAnonymitySet(selectedDenomination, provider)
      }
    } catch (error: any) {
      console.error('取款失败:', error)
      showErrorModal(error)
    } finally {
      setLoading(false)
    }
  }

  // 选择固定金额池
  const handleDenominationChange = async (index: number) => {
    setSelectedDenomination(index)
    const amount = DENOMINATIONS[index].amount
    setDepositAmount(amount)
    setAmountError('')

    // 更新匿名集
    if (provider) {
      await updateAnonymitySet(index, provider)
    }
  }

  // MAX 按钮处理函数
  const setMaxWrapAmount = () => {
    setWrapAmount(usdtBalance)
  }

  const setMaxUnwrapAmount = () => {
    setWrapAmount(cUsdtBalance)
  }

  const setMaxDepositAmount = () => {
    // 对于固定金额池，MAX 应该是固定金额（不超过余额）
    const fixedAmount = DENOMINATIONS[selectedDenomination].amount
    const maxAmount = parseFloat(cUsdtBalance) >= parseFloat(fixedAmount) ? fixedAmount : cUsdtBalance
    setDepositAmount(maxAmount)
    validateDepositAmount(maxAmount, selectedDenomination)
  }

  const setMaxBlurDepositAmount = () => {
    setDepositAmount(tokenBalance === '0' ? '0' : tokenBalance)
  }

  // 断开钱包连接
  // 切换到 Sepolia 网络
  const switchToSepolia = async () => {
    if (typeof window.ethereum === 'undefined') {
      setStatus('❌ Please install MetaMask or OKX Wallet first')
      return
    }

    setLoading(true)
    setStatus('🔄 Switching to Sepolia testnet...')

    try {
      // 尝试切换到 Sepolia
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0xaa36a7' }], // 11155111 的十六进制
      })
      setStatus('✨ Switched to Sepolia testnet!')

      // 切换成功后刷新页面以更新状态
      setTimeout(() => {
        window.location.reload()
      }, 1000)

    } catch (switchError: any) {
      // 如果 Sepolia 不存在，尝试添加
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: '0xaa36a7', // 11155111
                chainName: 'Sepolia test network',
                nativeCurrency: {
                  name: 'Sepolia ETH',
                  symbol: 'ETH',
                  decimals: 18,
                },
                rpcUrls: ['https://ethereum-sepolia-rpc.publicnode.com'],
                blockExplorerUrls: ['https://sepolia.etherscan.io'],
              },
            ],
          })
          setStatus('✨ Sepolia network added and switched successfully!')

          // 添加成功后刷新页面
          setTimeout(() => {
            window.location.reload()
          }, 1000)

        } catch (addError: any) {
          setStatus('❌ Failed to add network: ' + addError.message)
        }
      } else if (switchError.code === 4001) {
        setStatus('❌ User rejected network switch')
      } else {
        setStatus('❌ Failed to switch network: ' + switchError.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // 手动刷新余额
  const refreshBalance = async () => {
    if (!connected || !provider || !account) {
      setStatus('❌ Please connect wallet first')
      return
    }
    setStatus('🔄 Refreshing balances...')
    await updateBalance(account, provider)
    setStatus('✨ Balances updated')
  }

  // 监听账户和网络变化
  // Sync wagmi wallet connection with ethers
  useEffect(() => {
    const initializeWallet = async () => {
      if (connected && walletClient && publicClient && account) {
        try {
          // Convert wagmi clients to ethers provider/signer
          const ethersProvider = clientToProvider(publicClient as any)
          const ethersSigner = clientToSigner(walletClient as any)

          setProvider(ethersProvider)
          setSigner(ethersSigner)

          // Initialize rewards contract
          const rewards = new ethers.Contract(
            USDT_ADDRESSES.nullayerRewards,
            NullayerRewardsABI,
            ethersProvider
          )
          setRewardsContract(rewards)

          const network = await ethersProvider.getNetwork()
          setStatus(`${t.statusConnectedTo} ${network.name} (Chain ID: ${network.chainId})`)

          // Save wallet connection
          saveWalletConnection(account)

          // Initialize wallet data
          if (network.chainId !== 11155111n) {
            setStatus(t.statusSwitchToSepolia)
          } else {
            await updateBalance(account, ethersProvider)
            await updateUsdtBalance(account, ethersProvider)
            await updateAnonymitySet(selectedDenomination, ethersProvider)
            await updateGlobalAnonymitySet(ethersProvider)  // 添加：初始化全局匿名集
            await checkCUsdtApproval(account, ethersProvider)

            // 设置就绪状态（不需要 FHE 初始化，使用简化加密）
            setFhevmReady(true)
            setStatus(t.statusConnected)
          }
        } catch (error: any) {
          console.error('初始化钱包失败:', error)
          setStatus('❌ 初始化失败: ' + error.message)
        }
      } else if (!connected) {
        // Clear state when disconnected
        setProvider(null)
        setSigner(null)
        setRewardsContract(null)
        setTokenBalance('0')
        setEthBalance('0')
        setUsdtBalance('0')
        setCUsdtBalance('Private')
        setCUsdtBalanceRevealed(false)
        setWrapAmount('')
        setEncryptedAmount('')
        setUsdtApproved(false)
        setDepositAmount('')
        setSecret('')
        setNullifier('')
        setCommitment('')
        setRecipient('')
        setApproved(false)
        setFhevmReady(false)
        setGlobalAnonymitySet(0)  // 添加：清空全局匿名集
        clearWalletConnection()
        setStatus('')
      }
    }

    initializeWallet()
  }, [connected, account, walletClient, publicClient])

  // 初始化存款金额（根据选择的金额池）
  useEffect(() => {
    if (!depositAmount || depositAmount === '') {
      setDepositAmount(DENOMINATIONS[selectedDenomination].amount)
    }
  }, [selectedDenomination])

  // 当存款金额或账户变化时检查授权
  useEffect(() => {
    if (account && provider && depositAmount && activeTab === 'usdt') {
      checkCUsdtApproval(account, provider)
    }
  }, [depositAmount, account, provider, activeTab])

  // 下载密钥到本地文件
  const downloadKeys = (secret: string, nullifier: string, commitment: string, encAmt: string = '') => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const modeLabel = mixerMode === 'encrypted' ? 'encrypted' : 'fixed'
    const filename = `blur-mixer-keys-${modeLabel}-${timestamp}.json`

    const keyData: any = {
      secret: secret,
      nullifier: nullifier,
      commitment: commitment,
      mixerMode: mixerMode,
      timestamp: new Date().toISOString(),
      network: 'Sepolia Testnet',
      warning: '⚠️ 请妥善保管此文件！取款时需要所有密钥信息',
      instructions: {
        '1': '保存此文件到安全位置',
        '2': '取款时打开此文件获取密钥信息',
        '3': '不要分享此文件给任何人',
        '4': '如果丢失此文件，将无法取款'
      }
    }

    // 加密模式下添加加密信息（不保存明文金额）
    if ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && encAmt) {
      // ❌ 不保存明文金额：keyData.amount = depositAmount
      keyData.encryptedAmount = encAmt
      keyData.encryptedAmountHash = ethers.keccak256(encAmt)

      // 根据模式设置 mixer 地址
      if (mixerMode === 'complete-privacy') {
        keyData.mixerAddress = USDT_ADDRESSES.mixerCompletePrivacy
      } else if (mixerMode === 'fully-encrypted') {
        keyData.mixerAddress = USDT_ADDRESSES.mixerFullyEncrypted
      } else {
        keyData.mixerAddress = USDT_ADDRESSES.mixerEncrypted
      }

      keyData.privacyNote = language === 'zh' ?
        '⚠️ 测试模式：金额在链上可见。生产模式将完全加密。' :
        '⚠️ Test mode: Amount visible on-chain. Production will be fully encrypted.'
      // 只保存金额范围提示（不泄露具体值）
      const amt = parseFloat(depositAmount)
      keyData.amountRange = amt < 100 ? 'small' :
                           amt < 10000 ? 'medium' : 'large'
    } else if (mixerMode === 'fixed') {
      keyData.denominationIndex = selectedDenomination
      keyData.denominationAmount = DENOMINATIONS[selectedDenomination].amount
      keyData.mixerAddress = USDT_ADDRESSES.mixerFixed
    }

    const jsonString = JSON.stringify(keyData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * 生成 FHE inputProof (Zama 风格)
   * @returns inputProof bytes
   *
   * 当前（Sepolia）：返回空 proof '0x'
   * 未来（fhEVM）：使用 fhevmjs 生成真正的 proof
   */
  const generateInputProof = (): string => {
    // Sepolia 测试模式：不需要 proof
    return '0x'

    // 未来 fhEVM 实现：
    // const fhevm = await createFhevmInstance(...)
    // const encrypted = await fhevm.encrypt64(amount)
    // return encrypted.inputProof
  }

  // 处理金额输入变化
  const handleDepositAmountChange = (newAmount: string) => {
    setDepositAmount(newAmount)

    // 如果已经生成了密钥，且金额改变了，清除密钥
    if ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && commitment && encryptedAmount) {
      const currentAmountBigInt = ethers.parseUnits(newAmount || '0', 6)
      const currentEncryptedAmount = ethers.toBeHex(currentAmountBigInt, 32)

      if (encryptedAmount !== currentEncryptedAmount) {
        // 清除密钥
        setCommitment('')
        setSecret('')
        setNullifier('')
        setEncryptedAmount('')
        setStatus('⚠️ 金额已改变，请重新生成密钥')
      }
    }
  }

  // 生成随机密钥
  const generateKeys = () => {
    // 检查加密模式下是否输入了金额
    if ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && (!depositAmount || parseFloat(depositAmount) <= 0)) {
      showModal(
        t.modalTitleEnterAmount,
        language === 'zh' ? '加密金额模式下，请先输入存款金额再生成密钥' : 'In encrypted mode, please enter deposit amount before generating keys',
        'warning'
      )
      return
    }

    const newSecret = '0x' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')

    const newNullifier = '0x' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')

    setSecret(newSecret)
    setNullifier(newNullifier)

    let commitment: string
    let newEncryptedAmount: string = ''

    if (mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') {
      // ✨ 加密金额模式：Zama FHE 风格
      const amountBigInt = ethers.parseUnits(depositAmount, 6)

      // 生成 FHE 风格的密文（包含随机 handle 和类型标记）
      // 格式：[random_handle_20bytes][amount_8bytes][padding_1byte][type_marker_3bytes]
      // 看起来像：0x094c49b406781a09...010500（类似 Zama）

      // Generate random 20-byte handle
      const randomBytes = new Uint8Array(20)
      crypto.getRandomValues(randomBytes)
      const handle = Array.from(randomBytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')

      // Encode amount as 8 bytes
      const amountHex = amountBigInt.toString(16).padStart(16, '0')

      // Type marker: 010500 (euint64 type in Zama)
      const typeMarker = '010500'
      const padding = '00'

      // Combine to create FHE-style ciphertext
      newEncryptedAmount = '0x' + handle + amountHex + padding + typeMarker

      console.log('✨ FHE-style encrypted amount:', newEncryptedAmount)

      setEncryptedAmount(newEncryptedAmount)
    }

    // ✨ 新隐私模式：commitment 不包含金额哈希，允许 withdraw 时重新加密
    // Commitment = keccak256(secret, nullifier) 对所有模式统一
    commitment = ethers.keccak256(
      ethers.solidityPacked(['bytes32', 'bytes32'], [newSecret, newNullifier])
    )

    setCommitment(commitment)

    // 自动下载密钥文件
    downloadKeys(newSecret, newNullifier, commitment, newEncryptedAmount)

    const modeText = mixerMode === 'complete-privacy' ?
      `${depositAmount} cUSDT (${language === 'zh' ? '完整隐私 ↓⭐' : 'Complete Privacy ↓⭐'})` :
      (mixerMode === 'fully-encrypted' ?
        `${depositAmount} cUSDT (${language === 'zh' ? '完全加密 ⭐' : 'Fully Encrypted ⭐'})` :
        (mixerMode === 'encrypted' ?
          `${depositAmount} cUSDT` :
          `${DENOMINATIONS[selectedDenomination].amount} cUSDT`))

    setStatus(`✨ 密钥已生成并已下载到本地！金额: ${modeText}`)
  }

  // 授权代币
  const approveTokens = async () => {
    if (!connected || !signer || !account) {
      setStatus('❌ 请先连接钱包')
      return
    }

    if (!fhevmReady) {
      setStatus('❌ FHE 环境未就绪，请重新连接钱包')
      return
    }

    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setStatus('❌ 请输入有效的存款金额')
      return
    }

    setLoading(true)
    setStatus('📝 正在准备授权...')

    try {
      const tokenContract = new ethers.Contract(CONTRACT_ADDRESSES.token, BLUR_TOKEN_ABI, signer)
      const amount = BigInt(depositAmount)

      // 使用 FHE 加密金额
      if (isMockMode()) {
        setStatus('🧪 正在加密金额 (模拟模式)...')
      } else {
        setStatus('🔐 正在加密金额...')
      }

      const { encryptedAmount, inputProof } = await prepareEncryptedInput(
        amount,
        account,
        CONTRACT_ADDRESSES.token
      )

      console.log('加密结果:', { encryptedAmount, inputProof })

      if (isMockMode()) {
        console.warn('⚠️ 使用模拟加密格式 - 合约可能拒绝此格式')
      }

      // 调用授权（使用加密输入）
      setStatus('📝 正在发送授权交易...')
      const tx = await tokenContract.approve(
        CONTRACT_ADDRESSES.mixer,
        encryptedAmount,
        inputProof
      )

      setStatus('⏳ 等待授权交易确认...')
      const receipt = await tx.wait()

      setApproved(true)
      setStatus('✨ 代币授权成功！交易: ' + receipt.hash.substring(0, 10) + '...')

    } catch (error: any) {
      console.error('授权错误:', error)

      // 详细的错误提示
      if (error.message.includes('FHEVM')) {
        setStatus('❌ FHE 加密失败: ' + error.message)
      } else if (error.message.includes('user rejected')) {
        setStatus('❌ 用户取消了交易')
      } else if (error.message.includes('execution reverted')) {
        setStatus('❌ 授权失败: 合约执行被拒绝 - ' + error.message)
      } else {
        setStatus('❌ 授权失败: ' + error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // 存款
  const handleDeposit = async () => {
    if (!connected || !signer || !account) {
      setStatus('❌ 请先连接钱包')
      return
    }

    if (!fhevmReady) {
      setStatus('❌ FHE 环境未就绪，请重新连接钱包')
      return
    }

    if (!commitment || !depositAmount) {
      setStatus('❌ 请生成密钥并输入金额')
      return
    }

    if (!approved) {
      setStatus('❌ 请先授权代币')
      return
    }

    setLoading(true)
    setStatus('↓ 正在准备存款...')

    try {
      const mixerContract = new ethers.Contract(CONTRACT_ADDRESSES.mixer, BLUR_MIXER_ABI, signer)
      const amount = BigInt(depositAmount)

      // 使用 FHE 加密金额
      if (isMockMode()) {
        setStatus('🧪 正在加密存款金额 (模拟模式)...')
      } else {
        setStatus('🔐 正在加密存款金额...')
      }

      const { encryptedAmount, inputProof } = await prepareEncryptedInput(
        amount,
        account,
        CONTRACT_ADDRESSES.mixer
      )

      console.log('存款加密结果:', { commitment, encryptedAmount, inputProof })

      if (isMockMode()) {
        console.warn('⚠️ 使用模拟加密格式 - 合约可能拒绝此格式')
      }

      // 调用存款
      setStatus('↓ 正在发送存款交易...')
      const tx = await mixerContract.deposit(commitment, encryptedAmount, inputProof)

      setStatus('⏳ 等待存款交易确认...')
      const receipt = await tx.wait()

      setStatus('✨ 存款成功！交易: ' + receipt.hash)

      // 记录到积分系统
      if (rewardsContract) {
        try {
          setStatus('📊 正在记录积分...')
          const recordTx = await rewardsContract.recordDeposit(commitment, encryptedAmount)
          await recordTx.wait()
          console.log('✅ 积分记录成功')
        } catch (recordError: any) {
          console.error('积分记录失败:', recordError)
          // 不影响主流程，只是记录日志
        }
      }

      setDepositAmount('')
      setApproved(false)

      // 更新余额
      await updateBalance(account, provider)

      // 保存 commitment 到本地用于积分追踪
      try {
        const savedCommitments = JSON.parse(localStorage.getItem(`commitments_global`) || '[]')
        // 检查是否已存在（修复：使用 .some() 而不是 .includes()）
        const exists = savedCommitments.some((c: any) => c.commitment === commitment)
        if (!exists) {
          savedCommitments.push({
            commitment,
            depositTime: Date.now(),
            amount: depositAmount
          })
          localStorage.setItem(`commitments_global`, JSON.stringify(savedCommitments))
          console.log('✅ Commitment 已保存到 localStorage:', commitment)
        }
      } catch (e) {
        console.error('❌ 保存 commitment 失败:', e)
      }

      // 提示保存密钥
      setTimeout(() => {
        setStatus('⚠️ 请务必保存 Secret 和 Nullifier！1小时后可以取款')
      }, 3000)

    } catch (error: any) {
      console.error('存款错误:', error)

      // 详细的错误提示
      if (error.message.includes('FHEVM')) {
        setStatus('❌ FHE 加密失败: ' + error.message)
      } else if (error.message.includes('user rejected')) {
        setStatus('❌ 用户取消了交易')
      } else if (error.message.includes('commitment already used')) {
        setStatus('❌ 存款失败: 此 commitment 已被使用，请重新生成密钥')
      } else if (error.message.includes('execution reverted')) {
        setStatus('❌ 存款失败: 合约执行被拒绝 - ' + error.message)
      } else {
        setStatus('❌ 存款失败: ' + error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // 上传密钥文件并自动填充取款表单
  const handleUploadKeysFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        const keys = JSON.parse(content)

        console.log('📁 上传的密钥文件:', keys) // 调试日志

        // 验证文件格式
        if (!keys.secret || !keys.nullifier) {
          setStatus('❌ 密钥文件格式错误：缺少 secret 或 nullifier')
          return
        }

        // 自动填充表单
        setSecret(keys.secret)
        setNullifier(keys.nullifier)

        // 填充 encryptedAmount（适用于加密模式）
        if (keys.encryptedAmount) {
          setEncryptedAmount(keys.encryptedAmount)
          console.log('✨ 已填充 encryptedAmount:', keys.encryptedAmount)
        }

        const loadedFields = ['secret', 'nullifier']
        if (keys.encryptedAmount) loadedFields.push('encryptedAmount')

        setStatus(`✨ 已加载密钥文件 (${loadedFields.join(', ')})`)
      } catch (error: any) {
        console.error('❌ 文件解析错误:', error)
        setStatus('❌ 文件解析失败：' + error.message)
      }
    }

    reader.onerror = () => {
      setStatus('❌ 文件读取失败')
    }

    reader.readAsText(file)

    // 清空 input 以允许重复上传同一文件
    event.target.value = ''
  }

  // 取款
  const handleWithdraw = async () => {
    if (!connected || !signer) {
      setStatus('❌ 请先连接钱包')
      return
    }

    if (!secret || !nullifier || !recipient) {
      setStatus('❌ 请输入完整的取款信息')
      return
    }

    setLoading(true)
    setStatus('↑ Withdrawing...')

    try {
      const mixerContract = new ethers.Contract(CONTRACT_ADDRESSES.mixer, BLUR_MIXER_ABI, signer)

      const tx = await mixerContract.withdraw(secret, nullifier, recipient)

      setStatus('⏳ Waiting for withdrawal confirmation...')
      const receipt = await tx.wait()

      setStatus('✨ 取款成功！交易哈希: ' + receipt.hash)

      // 更新后端积分记录
      try {
        // 从 localStorage 获取对应的 commitment 和金额
        const commitments = JSON.parse(localStorage.getItem(`commitments_global`) || '[]')

        // 计算当前 withdraw 的 commitment（从 secret + nullifier）
        const withdrawCommitment = ethers.keccak256(
          ethers.solidityPacked(['bytes32', 'bytes32'], [secret, nullifier])
        )

        // 通过 commitment 精确匹配
        const matchedCommitment = commitments.find((c: any) =>
          c.commitment.toLowerCase() === withdrawCommitment.toLowerCase()
        )

        console.log('🔍 Matching commitment:', {
          withdrawCommitment,
          savedCommitments: commitments.map((c: any) => c.commitment),
          matched: !!matchedCommitment
        })

        if (matchedCommitment) {
          const response = await fetch(`${API_URL}/api/update-deposit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              commitment: matchedCommitment.commitment,
              address: recipient,
              amount: parseInt(matchedCommitment.amount)
            })
          })

          if (response.ok) {
            const result = await response.json()
            console.log(`✅ ${t.pointsUpdated}:`, result.points_earned)
            setTimeout(() => {
              setStatus(`✨ ${t.withdrawSuccessPoints} ${result.points_earned.toFixed(2)} ${t.pointsEarned} 🎁`)
            }, 2000)
          }
        } else {
          console.warn('⚠️ 未找到匹配的存款记录，可能是:', {
            reason1: '使用了不同的 secret/nullifier',
            reason2: '该存款是在积分系统上线前完成的',
            solution: '请确保使用正确的 secret 和 nullifier'
          })
        }
      } catch (apiError) {
        console.error('积分更新失败:', apiError)
        // 不影响主流程
      }

      setSecret('')
      setNullifier('')
      setRecipient('')
    } catch (error: any) {
      setStatus('❌ 取款失败: ' + error.message)
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Routes>
      {/* Home/Landing Page */}
      <Route path="/" element={<Landing onLaunchApp={() => navigate('/shield')} />} />

      {/* Shield Page */}
      <Route path="/shield" element={
        <>
          <Navbar
            onNavigateHome={() => navigate('/')}
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            showNavLinks={true}
          />

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        details={modalDetails}
        type={modalType}
        language={language}
      />

      <div className="app" style={{ paddingTop: '80px' }}>
        <main className="main">
        {/* 状态显示 */}
        {status && (
          <div className={`status-message ${status.includes('❌') ? 'error' : status.includes('⚠️') ? 'warning' : status.includes('✨') ? 'success' : 'info'}`}>
            {status}
          </div>
        )}

        {/* 显示余额和内容 */}
        <>
          {/* 余额显示 */}
          <div className="balance-grid">
              <div className="balance-item">
                <div className="balance-label">{t.ethBalance}</div>
                <div className="balance-value">{parseFloat(ethBalance).toFixed(4)}</div>
              </div>
              <div className="balance-item">
                <div className="balance-label">{t.usdtBalance}</div>
                <div className="balance-value">{parseFloat(usdtBalance).toFixed(2)}</div>
              </div>
              <div className="balance-item">
                <div className="balance-label">{t.cUsdtBalance}</div>
                <div className="balance-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {cUsdtBalanceRevealed ? parseFloat(cUsdtBalance).toFixed(2) : cUsdtBalance}
                  <button
                    onClick={cUsdtBalanceRevealed ? hideCUsdtBalance : revealCUsdtBalance}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.2em',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title={cUsdtBalanceRevealed ? (language === 'zh' ? '隐藏余额' : 'Hide balance') : (language === 'zh' ? '显示余额' : 'Reveal balance')}
                  >
                    {cUsdtBalanceRevealed ? '👁️' : '🔒'}
                  </button>
                </div>
              </div>
            </div>


        <div className="content-grid">
          {/* Wrap/Unwrap Card */}
          <div className="card">
            <h2>💱 Wrap/Unwrap USDT</h2>
            <div className="form-group">
              <label>{t.amount}</label>
              <input
                type="number"
                placeholder="e.g. 1000"
                value={wrapAmount}
                onChange={(e) => setWrapAmount(e.target.value)}
                disabled={loading}
              />
              <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💰 USDT: {parseFloat(usdtBalance).toFixed(2)}
                  <button
                    onClick={setMaxWrapAmount}
                    disabled={loading}
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.75em',
                      background: 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'var(--primary-hover)')}
                    onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'var(--primary)')}
                    title={language === 'zh' ? '用于封装：USDT → cUSDT' : 'For Wrap: USDT → cUSDT'}
                  >
                    Max
                  </button>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💰 cUSDT: {cUsdtBalanceRevealed ? parseFloat(cUsdtBalance).toFixed(2) : cUsdtBalance}
                  <button
                    onClick={setMaxUnwrapAmount}
                    disabled={loading || !cUsdtBalanceRevealed}
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.75em',
                      background: cUsdtBalanceRevealed ? 'var(--secondary)' : '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (loading || !cUsdtBalanceRevealed) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      opacity: cUsdtBalanceRevealed ? 1 : 0.5
                    }}
                    onMouseEnter={(e) => cUsdtBalanceRevealed && !loading && (e.currentTarget.style.background = '#6b7280')}
                    onMouseLeave={(e) => cUsdtBalanceRevealed && !loading && (e.currentTarget.style.background = 'var(--secondary)')}
                    title={
                      !cUsdtBalanceRevealed
                        ? (language === 'zh' ? '请先显示 cUSDT 余额（点击 👁️）' : 'Please reveal cUSDT balance first (click 👁️)')
                        : (language === 'zh' ? '用于解封装：cUSDT → USDT' : 'For Unwrap: cUSDT → USDT')
                    }
                  >
                    Max
                  </button>
                  <button
                    onClick={cUsdtBalanceRevealed ? hideCUsdtBalance : revealCUsdtBalance}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.2em',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title={cUsdtBalanceRevealed ? (language === 'zh' ? '隐藏余额' : 'Hide balance') : (language === 'zh' ? '显示余额' : 'Reveal balance')}
                  >
                    {cUsdtBalanceRevealed ? '👁️' : '🔒'}
                  </button>
                </span>
              </div>
              <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '5px', fontStyle: 'italic' }}>
                💡 {language === 'zh'
                  ? '提示：封装使用 USDT，解封装使用 cUSDT（需先显示余额）'
                  : 'Tip: Use USDT Max for Wrap, cUSDT Max for Unwrap (reveal balance first)'}
              </div>
            </div>
            <div className="btn-group">
              <button
                onClick={!connected ? openConnectModal : wrapUsdt}
                className="btn"
                disabled={connected && (loading || !wrapAmount)}
                title={
                  !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                  !wrapAmount ? (language === 'zh' ? '请输入金额' : 'Please enter amount') :
                  ''
                }
              >
                {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') :
                 loading ? t.processing : t.wrap}
              </button>
              <button
                onClick={!connected ? openConnectModal : unwrapUsdt}
                className="btn btn-secondary"
                disabled={connected && (loading || !wrapAmount)}
                title={
                  !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                  !wrapAmount ? (language === 'zh' ? '请输入金额' : 'Please enter amount') :
                  ''
                }
              >
                {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') :
                 loading ? t.processing : t.unwrap}
              </button>
            </div>
            <div className="info-box" style={{ marginTop: '15px' }}>
              <p style={{ fontSize: '0.85em', margin: 0 }}>
                💡 {language === 'zh' ? '先封装 USDT → cUSDT，然后存入隐私盾' : 'Wrap your USDT into confidential cUSDT before depositing into Shield'}
              </p>
            </div>
          </div>

          {/* Keys Card */}
          <div className="card">
            <h2>🔐 {language === 'zh' ? '生成密钥' : 'Generate Keys'}</h2>
            <button
              onClick={!connected ? openConnectModal : generateKeys}
              className="btn"
              disabled={connected && loading}
              title={
                !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                language === 'zh'
                  ? (depositAmount
                      ? `💡 根据当前金额 ${depositAmount} USDT 生成密钥`
                      : '💡 先输入存款金额，再生成密钥（加密模式需要）')
                  : (depositAmount
                      ? `💡 Generate keys based on amount ${depositAmount} USDT`
                      : '💡 Enter deposit amount first, then generate keys (required for encrypted mode)')
              }
            >
              {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') : t.generateNewKeys}
            </button>
            {secret && (
              <div className="key-display">
                <div className="key-item">
                  <div className="key-label">{t.secret}</div>
                  <div className="key-value">{secret.substring(0, 30)}...</div>
                </div>
                <div className="key-item">
                  <div className="key-label">{t.nullifier}</div>
                  <div className="key-value">{nullifier.substring(0, 30)}...</div>
                </div>
                <div className="key-item">
                  <div className="key-label">{t.commitment}</div>
                  <div className="key-value">{commitment.substring(0, 30)}...</div>
                </div>
                <p style={{ marginTop: '10px', color: 'var(--warning)', fontSize: '0.85em' }}>
                  ⚠️ {t.saveKeys}
                </p>
                <button
                  onClick={() => downloadKeys(secret, nullifier, commitment, encryptedAmount)}
                  className="btn btn-secondary"
                  style={{ marginTop: '10px' }}
                >
                  <span className="arrow-icon">↓</span> {t.redownloadKeys}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Deposit/Withdraw Grid */}
        <div className="content-grid">
          {/* Deposit Card */}
          <div className="card">
            <h2><span className="arrow-icon">↓</span> {language === 'zh' ? '存款 cUSDT' : 'Deposit cUSDT'}</h2>

            {(mixerMode === 'encrypted' || mixerMode === 'complete-privacy' || mixerMode === 'fully-encrypted') ? (
              /* 自由金额模式：encrypted/complete-privacy/fully-encrypted */
              <>
                <div className="form-group">
                  <label>{t.amount} (cUSDT)</label>
                  <div className="input-with-max">
                    <input
                      type="number"
                      placeholder={language === 'zh' ? '输入 1 到 1,000,000 USDT' : 'Enter 1 to 1,000,000 USDT'}
                      value={depositAmount}
                      onChange={(e) => handleDepositAmountChange(e.target.value)}
                      min="1"
                      max="1000000"
                      step="0.01"
                      disabled={loading}
                    />
                    <button
                      className="max-button"
                      onClick={() => handleDepositAmountChange(cUsdtBalance)}
                      disabled={loading}
                      type="button"
                    >
                      {t.max}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>💡 {language === 'zh' ? '可存入任意金额 (1-1,000,000 USDT)' : 'Any amount (1-1,000,000 USDT)'}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      💰 cUSDT: {cUsdtBalanceRevealed ? parseFloat(cUsdtBalance).toFixed(2) : cUsdtBalance}
                      <button
                        onClick={cUsdtBalanceRevealed ? hideCUsdtBalance : revealCUsdtBalance}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={cUsdtBalanceRevealed ? (language === 'zh' ? '隐藏余额' : 'Hide balance') : (language === 'zh' ? '显示余额' : 'Reveal balance')}
                      >
                        {cUsdtBalanceRevealed ? '👁️' : '🔒'}
                      </button>
                    </span>
                  </div>
                </div>

                {/* 全局匿名集显示 */}
                <div className="info-box" style={{ marginTop: '10px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9em' }}>
                      🌐 {language === 'zh' ? '全局匿名集' : 'Global Anonymity Set'}: <strong>{globalAnonymitySet}</strong> {t.deposits}
                    </span>
                    <span style={{
                      fontSize: '0.85em',
                      color: globalAnonymitySet >= 20 ? 'var(--success)' : 'var(--warning)'
                    }}>
                      {globalAnonymitySet >= 20 ? `✨ ${t.privacyStrong}` : `⚠️ ${t.privacyWeak}`}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)', marginTop: '8px' }}>
                    💡 {language === 'zh' ? '所有用户共享一个池子，隐私性比固定金额池提升 2-5 倍' : 'All users in one pool, 2-5x better privacy than fixed pools'}
                  </div>
                </div>
              </>
            ) : (
              /* 固定金额模式：选择金额池 */
              <>
                <div className="form-group">
                  <label>{t.selectPool}</label>
                  <select
                    value={selectedDenomination}
                    onChange={(e) => handleDenominationChange(Number(e.target.value))}
                    disabled={loading}
                    style={{ padding: '10px', fontSize: '14px', width: '100%' }}
                  >
                    {DENOMINATIONS.map(denom => (
                      <option key={denom.index} value={denom.index}>
                        {denom.label} - {language === 'zh' ? (denom.index === 0 ? '小额测试' : denom.index === 1 ? '推荐使用' : '大额交易') : (denom.index === 0 ? 'Small Test' : denom.index === 1 ? 'Recommended' : 'Large Amount')}
                        {denom.recommended && ' ⭐'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 匿名集显示 */}
                <div className="info-box" style={{ marginTop: '10px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9em' }}>
                      <span className="arrow-icon">↓</span> {t.anonymitySet}: <strong>{anonymitySet}</strong> {t.deposits}
                    </span>
                    <span style={{
                      fontSize: '0.85em',
                      color: anonymitySet >= 10 ? 'var(--success)' : 'var(--warning)'
                    }}>
                      {anonymitySet >= 10 ? `✨ ${t.privacyStrong}` : `⚠️ ${t.privacyWeak}`}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t.amount} (cUSDT)</label>
                  <div className="input-with-max">
                    <input
                      type="number"
                      placeholder={DENOMINATIONS[selectedDenomination].amount}
                      value={depositAmount}
                      onChange={(e) => {
                        handleDepositAmountChange(e.target.value)
                        validateDepositAmount(e.target.value, selectedDenomination)
                      }}
                      disabled={loading}
                    />
                    <button
                      className="max-button"
                      onClick={setMaxDepositAmount}
                      disabled={loading}
                      type="button"
                    >
                      {t.max}
                    </button>
                  </div>
                  {amountError && (
                    <div style={{ color: 'var(--error)', fontSize: '0.85em', marginTop: '5px' }}>
                      {amountError}
                    </div>
                  )}
                  <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>💡 {t.fixedAmount}: {DENOMINATIONS[selectedDenomination].amount} USDT</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      💰 cUSDT: {cUsdtBalanceRevealed ? parseFloat(cUsdtBalance).toFixed(2) : cUsdtBalance}
                      <button
                        onClick={cUsdtBalanceRevealed ? hideCUsdtBalance : revealCUsdtBalance}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={cUsdtBalanceRevealed ? (language === 'zh' ? '隐藏余额' : 'Hide balance') : (language === 'zh' ? '显示余额' : 'Reveal balance')}
                      >
                        {cUsdtBalanceRevealed ? '👁️' : '🔒'}
                      </button>
                    </span>
                  </div>
                </div>
              </>
            )}
            {!usdtApproved && (
              <div className="form-group">
                <button
                  onClick={!connected ? openConnectModal : approveCUsdtForMixer}
                  className="btn"
                  disabled={connected && (loading || !depositAmount)}
                  title={
                    !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                    !depositAmount ? (language === 'zh' ? '请先输入存款金额' : 'Please enter deposit amount first') :
                    ''
                  }
                >
                  {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') :
                   `📝 ${language === 'zh' ? '授权 cUSDT' : 'Approve cUSDT'}`}
                </button>
              </div>
            )}
            {usdtApproved && (
              <div style={{
                padding: '10px 15px',
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#8b5cf6',
                marginBottom: '15px',
                textAlign: 'center'
              }}>
                ✨ {t.alreadyApproved}
              </div>
            )}
            <button
              onClick={!connected ? openConnectModal : depositCUsdt}
              className="btn"
              disabled={connected && (loading || !commitment || !depositAmount || !usdtApproved)}
              title={
                !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                loading ? '' :
                !depositAmount ? (language === 'zh' ? '请先输入存款金额' : 'Please enter deposit amount first') :
                !commitment ? (language === 'zh' ? '⚠️ 请先根据金额生成密钥（Generate Keys 按钮）' : '⚠️ Please generate keys based on amount first (Generate Keys button)') :
                !usdtApproved ? (language === 'zh' ? '请先授权 cUSDT' : 'Please approve cUSDT first') :
                (language === 'zh' ? '点击存款到隐私盾' : 'Click to deposit to Shield')
              }
              style={{
                cursor: (connected && (loading || !commitment || !depositAmount || !usdtApproved)) ? 'not-allowed' : 'pointer'
              }}
            >
              {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') :
               loading ? `⏳ ${t.processing}` : <><span className="arrow-icon">↓</span> {t.deposit}</>}
            </button>
          </div>

          {/* Withdraw Card */}
          <div className="card">
            <h2><span className="arrow-icon">↑</span> {language === 'zh' ? '取款 cUSDT' : 'Withdraw cUSDT'}</h2>

            {/* 密钥文件上传按钮 */}
            <div className="form-group">
              <label>{language === 'zh' ? '快速导入' : 'Quick Import'}</label>
              <label
                htmlFor="keys-file-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '15px',
                  border: '2px dashed var(--primary)',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(139, 92, 246, 0.05)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: loading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.15)'
                    e.currentTarget.style.borderColor = 'var(--primary-hover)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.05)'
                    e.currentTarget.style.borderColor = 'var(--primary)'
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 8L12 3L7 8" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V15" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, color: 'var(--primary)' }}>
                    {language === 'zh' ? '📂 点击上传密钥文件' : '📂 Click to Upload Keys File'}
                  </div>
                  <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)', marginTop: '3px' }}>
                    {language === 'zh' ? '支持 .json 文件，自动填充所有字段' : 'Supports .json files, auto-fills all fields'}
                  </div>
                </div>
              </label>
              <input
                id="keys-file-upload"
                type="file"
                accept=".json"
                onChange={handleUploadKeysFile}
                disabled={loading}
                style={{ display: 'none' }}
              />
            </div>

            <div className="form-group">
              <label>{t.secret}</label>
              <input
                type="text"
                placeholder={language === 'zh' ? '您的密钥' : 'Your secret key'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>{t.nullifier}</label>
              <input
                type="text"
                placeholder={language === 'zh' ? '您的nullifier' : 'Your nullifier'}
                value={nullifier}
                onChange={(e) => setNullifier(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Encrypted Amount input - only for encrypted modes */}
            {(mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && (
              <div className="form-group">
                <label>{language === 'zh' ? '加密金额' : 'Encrypted Amount'}</label>
                <input
                  type="text"
                  placeholder={language === 'zh' ? '从密钥文件中的 encryptedAmount (0x...)' : 'encryptedAmount from key file (0x...)'}
                  value={encryptedAmount}
                  onChange={(e) => setEncryptedAmount(e.target.value)}
                  disabled={loading}
                />
                <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '5px' }}>
                  💡 {language === 'zh' ? '可以从密钥文件中复制 encryptedAmount，或手动输入' : 'Copy from key file or paste manually'}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t.recipientAddress}</label>
              <input
                type="text"
                placeholder="0x..."
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              onClick={!connected ? openConnectModal : withdrawCUsdt}
              className="btn"
              disabled={
                connected && (
                  loading ||
                  !secret ||
                  !nullifier ||
                  !recipient ||
                  ((mixerMode === 'encrypted' || mixerMode === 'fully-encrypted' || mixerMode === 'complete-privacy') && !encryptedAmount)
                )
              }
              title={
                !connected ? (language === 'zh' ? '点击连接钱包' : 'Click to connect wallet') :
                !secret || !nullifier ? (language === 'zh' ? '请输入密钥信息' : 'Please enter keys') :
                !recipient ? (language === 'zh' ? '请输入接收地址' : 'Please enter recipient address') :
                ''
              }
            >
              {!connected ? (language === 'zh' ? '👛 连接钱包' : '👛 Connect Wallet') :
               loading ? `⏳ ${t.processing}` : <><span className="arrow-icon">↑</span> {t.withdraw}</>}
            </button>
          </div>
        </div>

        {/* USDT Privacy Flow Info */}
        <div className="info-box">
          <h3>{t.infoHowItWorks}</h3>
          <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>{t.infoPrivacyFlow}</p>
          <ul>
            <li>{t.infoStep1}</li>
            <li>{t.infoStep2}</li>
            <li>{t.infoStep3}</li>
            <li>{t.infoStep4}</li>
          </ul>
        </div>
        </>
</main>
</div>
        </>
      } />

      {/* Mining Page (Global Stats) */}
      <Route path="/mining" element={
        <>
          <Navbar
            onNavigateHome={() => navigate('/')}
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            showNavLinks={true}
          />

          <Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title={modalTitle}
            message={modalMessage}
            details={modalDetails}
            type={modalType}
            language={language}
          />

          <div className="app" style={{ paddingTop: '80px' }}>
            <main className="main">
              {/* Status Display */}
              {status && (
                <div className={`status-message ${status.includes('❌') ? 'error' : status.includes('⚠️') ? 'warning' : status.includes('✨') ? 'success' : 'info'}`}>
                  {status}
                </div>
              )}

              {/* Mining Dashboard */}
              {connected && rewardsContract ? (
                <MiningDashboard
                  provider={provider}
                  account={account || ''}
                  rewardsContract={rewardsContract}
                  language={language}
                />
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
                  <h2>💎 {language === 'zh' ? '挖矿数据面板' : 'Mining Dashboard'}</h2>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '20px', fontSize: '1.1rem' }}>
                    {language === 'zh'
                      ? '请先连接钱包以查看挖矿数据'
                      : 'Please connect your wallet to view mining data'}
                  </p>
                  <button
                    className="btn"
                    onClick={openConnectModal}
                    style={{ marginTop: '30px', maxWidth: '300px', margin: '30px auto 0' }}
                  >
                    👛 {language === 'zh' ? '连接钱包' : 'Connect Wallet'}
                  </button>
                </div>
              )}
            </main>
          </div>
        </>
      } />

      {/* Leaderboard Page */}
      <Route path="/leaderboard" element={
        <>
          <Navbar
            onNavigateHome={() => navigate('/')}
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            showNavLinks={true}
          />

          <div className="app" style={{ paddingTop: '80px' }}>
            <main className="main">
              {/* Status Display */}
              {status && (
                <div className={`status-message ${status.includes('❌') ? 'error' : status.includes('⚠️') ? 'warning' : status.includes('✨') ? 'success' : 'info'}`}>
                  {status}
                </div>
              )}

              {/* Leaderboard */}
              <Leaderboard language={language} />
            </main>
          </div>
        </>
      } />

      {/* Personal Dashboard (My Stats) */}
      <Route path="/dashboard" element={
        <>
          <Navbar
            onNavigateHome={() => navigate('/')}
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            showNavLinks={true}
          />

          <Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title={modalTitle}
            message={modalMessage}
            details={modalDetails}
            type={modalType}
            language={language}
          />

          <div className="app" style={{ paddingTop: '80px' }}>
            <main className="main">
              {/* Status Display */}
              {status && (
                <div className={`status-message ${status.includes('❌') ? 'error' : status.includes('⚠️') ? 'warning' : status.includes('✨') ? 'success' : 'info'}`}>
                  {status}
                </div>
              )}

              {/* Personal Dashboard */}
              <MyDashboard
                provider={provider}
                account={account || null}
                rewardsContract={rewardsContract}
                language={language}
              />
            </main>
          </div>
        </>
      } />

      {/* Redirect old routes */}
      <Route path="/app" element={<Navigate to="/shield" replace />} />
      <Route path="/mixer" element={<Navigate to="/shield" replace />} />
    </Routes>
  )
}

export default App
